import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, MapPin, Package, Truck, Clock } from 'lucide-react';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TrackingPage() {
  const [searchParams] = useSearchParams();
  const initialTn = searchParams.get('tn') || '';
  const [trackingNumber, setTrackingNumber] = useState(initialTn);
  const [trackingResult, setTrackingResult] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const autoTrackedRef = useRef(false);

  const handleTrack = async (tn) => {
    const value = (tn ?? trackingNumber).trim();
    if (!value) {
      toast.error('Please enter a tracking number');
      return;
    }

    setIsTracking(true);
    setTrackingResult(null); // Clear previous results
    
    try {
      const response = await fetch(`${API}/tracking/${value}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          toast.error('Tracking number not found. Please check and try again.');
        } else {
          toast.error('Failed to fetch tracking information. Please try again.');
        }
        setIsTracking(false);
        return;
      }

      const data = await response.json();
      
      // Process the history to add completed status based on current order status
      const statusOrder = ['Order Placed', 'Picked Up', 'In Transit', 'Out for Delivery', 'Delivered'];
      const currentStatusIndex = statusOrder.indexOf(data.status);
      
      const processedHistory = data.history.map((item, index) => {
        const itemStatusIndex = statusOrder.indexOf(item.status);
        return {
          ...item,
          completed: itemStatusIndex <= currentStatusIndex,
          time: new Date(item.created_at).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          })
        };
      });

      setTrackingResult({
        ...data,
        history: processedHistory,
        created_at: new Date(data.created_at).toLocaleString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })
      });
      
      toast.success('Shipment found!');
    } catch (error) {
      console.error('Error tracking shipment:', error);
      toast.error('Failed to connect to tracking service. Please try again.');
    } finally {
      setIsTracking(false);
    }
  };

  // Auto-track when ?tn=... is in URL (admin deep link)
  useEffect(() => {
    if (initialTn && !autoTrackedRef.current) {
      autoTrackedRef.current = true;
      handleTrack(initialTn);
    }
  }, [initialTn]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Order Placed':
        return Package;
      case 'Picked Up':
        return MapPin;
      case 'In Transit':
        return Truck;
      case 'Out for Delivery':
        return Truck;
      case 'Delivered':
        return Package;
      default:
        return Package;
    }
  };

  return (
    <Layout>
      {/* Hero Section with Image */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-hero z-10" />
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/6169674/pexels-photo-6169674.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
            }}
          />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/10 blur-[120px] rounded-full z-10" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16 animate-slide-up">
            <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10">
              Track Shipment
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Real-Time <span className="text-gradient">Tracking</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Stay informed every step of the way. Track your shipment in real-time with our advanced GPS tracking system. Get instant updates on your package location, estimated delivery time, and delivery status.
            </p>
          </div>

          {/* Quick Features */}
          <div className="grid grid-cols-3 gap-6 max-w-3xl mx-auto animate-slide-up" style={{ animationDelay: '0.1s' }}>
            <Card className="p-6 bg-card/80 backdrop-blur-sm border-border/50 text-center">
              <MapPin className="h-8 w-8 text-primary mx-auto mb-2" />
              <div className="text-sm font-semibold">Live GPS</div>
              <div className="text-xs text-muted-foreground">Real-time location</div>
            </Card>
            <Card className="p-6 bg-card/80 backdrop-blur-sm border-border/50 text-center">
              <Clock className="h-8 w-8 text-primary mx-auto mb-2" />
              <div className="text-sm font-semibold">Accurate ETA</div>
              <div className="text-xs text-muted-foreground">Precise timing</div>
            </Card>
            <Card className="p-6 bg-card/80 backdrop-blur-sm border-border/50 text-center">
              <Package className="h-8 w-8 text-primary mx-auto mb-2" />
              <div className="text-sm font-semibold">Status Updates</div>
              <div className="text-xs text-muted-foreground">Instant notifications</div>
            </Card>
          </div>
        </div>
      </section>

      {/* Tracking Input */}
      <section className="pb-12 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <Card className="scroll-animate max-w-2xl mx-auto p-8 bg-card/80 backdrop-blur-sm border-border/50">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input 
                  placeholder="Enter tracking number (e.g., SYN-2026-XXXXX)"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleTrack()}
                  data-testid="tracking-input"
                  className="pl-10 h-12 bg-background border-border/50 focus:border-primary transition-colors"
                />
              </div>
              <Button 
                onClick={() => handleTrack()}
                disabled={isTracking}
                size="lg"
                data-testid="track-shipment-btn"
                className="bg-gradient-primary hover:shadow-glow transition-all duration-300"
              >
                {isTracking ? 'Tracking...' : 'Track Shipment'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Enter your tracking number to see shipment details and real-time status updates
            </p>
          </Card>
        </div>
      </section>

      {/* Tracking Results */}
      {trackingResult && (
        <section className="pb-24 relative overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto space-y-6 animate-slide-up">
              {/* Status Summary Card */}
              <Card className="p-6 bg-gradient-card backdrop-blur-sm border-primary/20">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Tracking Number</div>
                    <div className="text-2xl font-bold text-gradient mb-4">{trackingResult.tracking_number}</div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-muted-foreground mb-1">Customer</div>
                        <div className="font-medium">{trackingResult.customer_name}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground mb-1">Order Date</div>
                        <div className="font-medium">{trackingResult.created_at}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Status</div>
                      <Badge className="bg-primary/20 text-primary border-primary/30 text-base px-3 py-1">
                        <Truck className="h-4 w-4 mr-2" />
                        {trackingResult.status}
                      </Badge>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Pickup Location</div>
                      <div className="font-medium flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-primary" />
                        {trackingResult.pickup_location}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Delivery Location</div>
                      <div className="font-medium flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-secondary" />
                        {trackingResult.delivery_location}
                      </div>
                    </div>
                    {trackingResult.weight && (
                      <div>
                        <div className="text-sm text-muted-foreground mb-1">Weight</div>
                        <div className="font-medium">{trackingResult.weight} lbs</div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>

              {/* Timeline */}
              <Card className="p-6 md:p-8 bg-card/80 backdrop-blur-sm border-border/50">
                <h3 className="text-xl font-semibold mb-6">Shipment Timeline</h3>
                <div className="space-y-6">
                  {trackingResult.history.map((event, index) => {
                    const Icon = getStatusIcon(event.status);
                    return (
                      <div key={index} className="flex gap-4 relative">
                        {/* Connector line */}
                        {index < trackingResult.history.length - 1 && (
                          <div 
                            className={`absolute left-6 top-14 bottom-0 w-0.5 ${
                              event.completed ? 'bg-primary/50' : 'bg-border'
                            }`} 
                          />
                        )}
                        
                        {/* Icon */}
                        <div className={`relative z-10 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                          event.completed 
                            ? 'bg-gradient-primary shadow-glow' 
                            : 'bg-muted border border-border'
                        }`}>
                          <Icon className={`h-6 w-6 ${
                            event.completed ? 'text-primary-foreground' : 'text-muted-foreground'
                          }`} />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 pb-6">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-1">
                            <h4 className={`font-semibold text-lg ${
                              event.completed ? 'text-foreground' : 'text-muted-foreground'
                            }`}>
                              {event.status}
                            </h4>
                            <span className="text-sm text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {event.time}
                            </span>
                          </div>
                          {event.location && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                              <MapPin className="h-3 w-3" />
                              {event.location}
                            </p>
                          )}
                          {event.description && (
                            <p className="text-sm text-muted-foreground">
                              {event.description}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Help Section */}
              <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
                <h4 className="font-semibold mb-3">Need Help?</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  If you have questions about your shipment or need to make changes to your delivery, our support team is here to help 24/7.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a href="tel:(647)-642-0062">
                    <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 w-full sm:w-auto">
                      Call: (647)-642-0062
                    </Button>
                  </a>
                  <a href="mailto:info@synclinelogistics.com">
                    <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 w-full sm:w-auto">
                      Email Support
                    </Button>
                  </a>
                </div>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Info Section - shown when no tracking result */}
      {!trackingResult && (
        <section className="py-24 bg-muted/30 relative overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12 scroll-animate">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                How <span className="text-gradient">Tracking Works</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Our advanced tracking system keeps you informed at every stage of your delivery
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-16">
              {[
                {
                  icon: Package,
                  title: 'Real-Time Updates',
                  description: 'Get instant notifications at every step of your delivery journey with SMS and email alerts'
                },
                {
                  icon: MapPin,
                  title: 'GPS Tracking',
                  description: 'See your package location in real-time with live GPS coordinates and route information'
                },
                {
                  icon: Clock,
                  title: 'Accurate ETAs',
                  description: 'Receive precise estimated delivery times based on actual routes and traffic conditions'
                }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card 
                    key={index}
                    className="scroll-animate p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300 text-center hover:shadow-glow group"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="p-3 rounded-xl bg-gradient-primary inline-flex mb-4 group-hover:shadow-glow transition-all">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>
                    <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </Card>
                );
              })}
            </div>

            {/* Tracking Image */}
            <div className="scroll-animate max-w-4xl mx-auto">
              <Card className="overflow-hidden bg-card/50 backdrop-blur-sm border-border/50">
                <img 
                  src="https://images.unsplash.com/photo-1543499459-d1460946bdc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxkZWxpdmVyeSUyMHBlcnNvbnxlbnwwfHx8fDE3NzgxMDgyMTJ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Professional Delivery"
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Professional Service, Every Time</h3>
                  <p className="text-muted-foreground">
                    Our GPS-equipped fleet and professional drivers ensure your packages are tracked from pickup to delivery with complete transparency and reliability.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
